(() => {
  'use strict';

  const GRID_ELEMENT = 'collection-grid';
  const TOOLBAR_ELEMENT = 'collection-toolbar';
  const PRICE_DEBOUNCE_MS = 700;
  const FILTER_MODE_SUBMIT = 'submit';
  const DRAWER_QUERY = '(max-width: 989px)';
  const SORT_EVENT = 'collection:sort-change';
  const DRAWER_EVENT = 'collection:open-filters';
  const GRID_REGIONS = ['[data-cg-active]', '[data-cg-filters]', '[data-cg-footer]', '[data-cg-results]'];
  const TOOLBAR_REGIONS = ['[data-cg-total-text]', '[data-cg-toggle-label]'];
  const GROUP_ROW = '[data-cg-group-row]';
  const GROUP_TOGGLE = '[data-cg-group]';
  const GROUP_MEMBER = '[data-cg-group-member]';
  const GROUP_MIXED = '[data-cg-group-mixed]';

  const findToolbar = () => document.querySelector('[data-cg-toolbar]');

  /* Toolbar: result count, filter button (mobile) and an accessible custom sort listbox. */
  class CollectionToolbar extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.select = this.querySelector('[data-cg-sort]');
      this.button = this.querySelector('[data-cg-sort-button]');
      this.list = this.querySelector('[data-cg-sort-list]');
      this.current = this.querySelector('[data-cg-sort-current]');
      this.activeIndex = -1;

      const options = { signal: this.controller.signal };
      this.querySelector('[data-cg-drawer-open]')?.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent(DRAWER_EVENT));
      }, options);

      if (!this.select || !this.button || !this.list) return;
      this.setAttribute('data-enhanced', '');
      this.button.addEventListener('click', () => (this.isOpen ? this.close() : this.open()), options);
      this.button.addEventListener('keydown', (event) => this.onButtonKeydown(event), options);
      this.list.addEventListener('keydown', (event) => this.onListKeydown(event), options);
      this.list.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.choose(option);
      }, options);
      document.addEventListener('click', (event) => {
        if (this.isOpen && !this.contains(event.target)) this.close(false);
      }, options);
      // Without the grid section on the page, fall back to the native GET form.
      this.select.addEventListener('change', () => {
        if (!document.querySelector(GRID_ELEMENT)) this.querySelector('[data-cg-sort-form]')?.requestSubmit();
      }, options);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    get options() {
      return [...this.list.querySelectorAll('[role="option"]')];
    }

    get isOpen() {
      return !this.list.hidden;
    }

    get value() {
      return this.select?.value || '';
    }

    open() {
      const selected = this.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
      this.list.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      this.setActive(selected >= 0 ? selected : 0);
      this.list.focus();
    }

    close(returnFocus = true) {
      this.list.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      this.list.removeAttribute('aria-activedescendant');
      if (returnFocus) this.button.focus();
    }

    setActive(index) {
      const options = this.options;
      if (!options.length) return;
      this.activeIndex = (index + options.length) % options.length;
      options.forEach((option, i) => option.classList.toggle('is-active', i === this.activeIndex));
      const active = options[this.activeIndex];
      this.list.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    choose(option) {
      const value = option.dataset.value;
      this.close();
      if (value === this.select.value) return;
      this.setValue(value);
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      document.dispatchEvent(new CustomEvent(SORT_EVENT, { detail: { value } }));
    }

    setValue(value) {
      if (!this.select) return;
      this.select.value = value;
      this.options?.forEach((option) => {
        const selected = option.dataset.value === value;
        option.setAttribute('aria-selected', String(selected));
        if (selected && this.current) this.current.textContent = option.textContent.trim();
      });
    }

    onButtonKeydown(event) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        this.open();
      }
    }

    onListKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.setActive(this.activeIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setActive(this.activeIndex - 1);
          break;
        case 'Home':
          event.preventDefault();
          this.setActive(0);
          break;
        case 'End':
          event.preventDefault();
          this.setActive(this.options.length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (this.options[this.activeIndex]) this.choose(this.options[this.activeIndex]);
          break;
        case 'Escape':
          event.preventDefault();
          this.close();
          break;
        case 'Tab':
          this.close(false);
          break;
        default:
          break;
      }
    }
  }

  /* Grid: AJAX filtering via the Section Rendering API, history, load more and the mobile drawer. */
  class CollectionGrid extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.requestController = null;
      this.priceTimer = null;
      this.drawer = this.querySelector('[data-cg-drawer]');
      this.drawerQuery = window.matchMedia(DRAWER_QUERY);

      const options = { signal: this.controller.signal };
      this.addEventListener('change', (event) => this.onChange(event), options);
      this.addEventListener('input', (event) => this.onInput(event), options);
      this.addEventListener('submit', (event) => this.onSubmit(event), options);
      this.addEventListener('click', (event) => this.onClick(event), options);
      window.addEventListener('popstate', () => this.render(window.location.search.slice(1), { updateHistory: false }), options);
      document.addEventListener(SORT_EVENT, () => this.applyForm(), options);
      document.addEventListener(DRAWER_EVENT, () => this.openDrawer(), options);
      this.drawerQuery.addEventListener('change', () => {
        if (!this.drawerQuery.matches) this.closeDrawer();
      }, options);
      this.drawer?.addEventListener('click', (event) => {
        // A click on the <dialog> element itself is a click on the backdrop.
        if (event.target === this.drawer) this.closeDrawer();
      }, options);
      this.drawer?.addEventListener('close', () => document.documentElement.classList.remove('cg-drawer-open'), options);

      this.applyMixedGroups();
      this.updateViewResultsLabel();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.requestController?.abort();
      window.clearTimeout(this.priceTimer);
    }

    get sectionId() {
      return this.dataset.sectionId;
    }

    get applyOnSubmit() {
      return this.dataset.filterMode === FILTER_MODE_SUBMIT;
    }

    onChange(event) {
      const target = event.target;
      if (!target.closest('[data-cg-form]')) return;
      if (target.matches(GROUP_TOGGLE)) this.syncGroup(target);
      window.clearTimeout(this.priceTimer);
      if (this.applyOnSubmit) {
        this.markPending();
        return;
      }
      this.applyForm();
    }

    /* A color group toggle has no name: the form submits its hidden member checkboxes instead. */
    syncGroup(toggle) {
      toggle.closest(GROUP_ROW)?.querySelectorAll(GROUP_MEMBER).forEach((member) => {
        member.checked = toggle.checked;
      });
    }

    /* Groups with only some colors active can't be expressed in HTML, only via the indeterminate property. */
    applyMixedGroups() {
      this.querySelectorAll(GROUP_MIXED).forEach((toggle) => {
        toggle.indeterminate = true;
      });
    }

    onInput(event) {
      if (!event.target.matches('[data-cg-price-input]')) return;
      window.clearTimeout(this.priceTimer);
      if (this.applyOnSubmit) {
        this.markPending();
        return;
      }
      this.priceTimer = window.setTimeout(() => this.applyForm(), PRICE_DEBOUNCE_MS);
    }

    /* Unapplied selections exist: offer "clear all" so they can be discarded. */
    markPending() {
      this.querySelector('[data-cg-clear-all]')?.removeAttribute('hidden');
    }

    onSubmit(event) {
      if (!event.target.matches('[data-cg-form]')) return;
      event.preventDefault();
      window.clearTimeout(this.priceTimer);
      this.applyForm();
    }

    onClick(event) {
      const link = event.target.closest('[data-cg-link]');
      if (link) {
        event.preventDefault();
        const params = new URL(link.href, window.location.origin).searchParams;
        this.removeParams(params, link.dataset.cgRemove);
        this.render(params.toString());
        return;
      }
      const loadMore = event.target.closest('[data-cg-load-more]');
      if (loadMore) {
        event.preventDefault();
        this.loadMore(loadMore);
        return;
      }
      if (event.target.closest('[data-cg-drawer-close]')) this.closeDrawer();
    }

    /* Group chips link to the URL without their first color and list the other colors to strip as JSON pairs. */
    removeParams(params, rawPairs) {
      if (!rawPairs) return;
      let pairs;
      try {
        pairs = JSON.parse(rawPairs);
      } catch (error) {
        console.error(`<${GRID_ELEMENT}>: invalid data-cg-remove value`, error);
        return;
      }
      if (!Array.isArray(pairs)) return;

      pairs.forEach((pair) => {
        if (!Array.isArray(pair) || pair.length !== 2) return;
        const [key, value] = pair.map(String);
        const kept = params.getAll(key).filter((current) => current !== value);
        params.delete(key);
        kept.forEach((current) => params.append(key, current));
      });
    }

    openDrawer() {
      if (!this.drawer || this.drawer.open || !this.drawerQuery.matches) return;
      this.drawer.showModal();
      document.documentElement.classList.add('cg-drawer-open');
    }

    closeDrawer() {
      if (this.drawer?.open) this.drawer.close();
    }

    buildQuery() {
      const params = new URLSearchParams();
      const form = this.querySelector('[data-cg-form]');
      if (form) {
        for (const [key, value] of new FormData(form)) {
          if (key === 'sort_by') continue;
          if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
        }
      }
      const sort = findToolbar()?.value || form?.querySelector('[data-cg-sort-input]')?.value;
      if (sort) params.set('sort_by', sort);
      return params.toString();
    }

    applyForm() {
      this.render(this.buildQuery());
    }

    async fetchSections(pathAndQuery) {
      this.requestController?.abort();
      this.requestController = new AbortController();
      const toolbar = findToolbar();
      const sectionIds = [this.sectionId, toolbar?.dataset.sectionId].filter(Boolean);
      const url = new URL(pathAndQuery, window.location.origin);
      url.searchParams.set('sections', sectionIds.join(','));

      const response = await fetch(url.toString(), { signal: this.requestController.signal });
      if (!response.ok) throw new Error(`Section request failed with status ${response.status}`);
      const payload = await response.json();
      const parse = (id) => (payload[id] ? new DOMParser().parseFromString(payload[id], 'text/html') : null);
      const gridDoc = parse(this.sectionId);
      const grid = gridDoc?.querySelector(GRID_ELEMENT);
      if (!grid) throw new Error('Section response did not contain the collection grid');
      return { grid, toolbar: toolbar ? parse(toolbar.dataset.sectionId)?.querySelector(TOOLBAR_ELEMENT) : null };
    }

    async render(query, { updateHistory = true } = {}) {
      const path = `${window.location.pathname}${query ? `?${query}` : ''}`;
      this.setLoading(true);
      this.clearError();

      try {
        const source = await this.fetchSections(path);
        const openState = this.captureOpenState();
        const focusedId = document.activeElement?.id;

        this.swap(this, source.grid, GRID_REGIONS);
        const toolbar = findToolbar();
        if (toolbar && source.toolbar) {
          this.swap(toolbar, source.toolbar, TOOLBAR_REGIONS);
          const total = source.toolbar.querySelector('[data-cg-total]')?.dataset.cgTotal;
          if (total !== undefined) toolbar.querySelector('[data-cg-total]')?.setAttribute('data-cg-total', total);
          const nextSort = source.toolbar.querySelector('[data-cg-sort]')?.value;
          if (nextSort && typeof toolbar.setValue === 'function') toolbar.setValue(nextSort);
        }

        this.restoreOpenState(openState);
        this.applyMixedGroups();
        this.updateViewResultsLabel();
        if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
        if (updateHistory) window.history.pushState({}, '', path);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(`<${GRID_ELEMENT}>: filter update failed`, error);
        this.showError();
      } finally {
        this.setLoading(false);
      }
    }

    swap(target, source, selectors) {
      selectors.forEach((selector) => {
        const current = target.querySelector(selector);
        const next = source.querySelector(selector);
        if (current && next) current.innerHTML = next.innerHTML;
      });
    }

    async loadMore(button) {
      const wrap = button.closest('[data-cg-load-more-wrap]');
      const grid = this.querySelector('[data-cg-grid]');
      if (!grid || button.getAttribute('aria-busy') === 'true') return;

      button.setAttribute('aria-busy', 'true');
      this.clearError();
      try {
        const next = new URL(button.href, window.location.origin);
        next.searchParams.set('section_id', this.sectionId);
        const response = await fetch(next.toString());
        if (!response.ok) throw new Error(`Load more failed with status ${response.status}`);
        const source = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector(GRID_ELEMENT);
        if (!source) throw new Error('Load more response did not contain the collection grid');

        const newItems = [...(source.querySelector('[data-cg-grid]')?.children || [])];
        grid.append(...newItems);
        const nextWrap = source.querySelector('[data-cg-load-more-wrap]');
        if (nextWrap) wrap?.replaceWith(nextWrap);
        else wrap?.remove();
        // Keep keyboard users in place: move focus to the first newly loaded product.
        newItems[0]?.querySelector('.collection-card__title a')?.focus({ preventScroll: true });
      } catch (error) {
        console.error(`<${GRID_ELEMENT}>: load more failed`, error);
        button.removeAttribute('aria-busy');
        this.showError();
      }
    }

    updateViewResultsLabel() {
      const button = this.querySelector('[data-cg-view-results]');
      const total = findToolbar()?.querySelector('[data-cg-total]')?.dataset.cgTotal;
      if (button && button.dataset.label && total !== undefined) {
        button.textContent = button.dataset.label.replace('[count]', total);
      }
    }

    captureOpenState() {
      const state = new Map();
      this.querySelectorAll('[data-filter-id]').forEach((details) => state.set(details.dataset.filterId, details.open));
      return state;
    }

    restoreOpenState(state) {
      this.querySelectorAll('[data-filter-id]').forEach((details) => {
        if (state.has(details.dataset.filterId)) details.open = state.get(details.dataset.filterId);
      });
    }

    setLoading(isLoading) {
      this.toggleAttribute('data-loading', isLoading);
      this.querySelector('[data-cg-results]')?.setAttribute('aria-busy', String(isLoading));
    }

    showError() {
      this.clearError();
      const message = document.createElement('p');
      message.className = 'cg__error';
      message.setAttribute('role', 'alert');
      message.dataset.cgError = '';
      message.textContent = this.dataset.errorMessage || 'Something went wrong. Please try again.';
      this.querySelector('[data-cg-results]')?.prepend(message);
    }

    clearError() {
      this.querySelectorAll('[data-cg-error]').forEach((el) => el.remove());
    }
  }

  if (!customElements.get(TOOLBAR_ELEMENT)) customElements.define(TOOLBAR_ELEMENT, CollectionToolbar);
  if (!customElements.get(GRID_ELEMENT)) customElements.define(GRID_ELEMENT, CollectionGrid);
})();
